<template>
  <div v-if="!loading">
    <b-row>
      <b-col class="col-6">
        <b-form-group :label="$t('field.company')">
          <multi-select
            v-model="company"
            :multiple="false"
            :options="companies"
            :allow-empty="false"
            :searchable="true"
            :placeholder="$t('actions.pick_company')"
            :show-labels="false"
            label="label"
            track-by="id"
            @input="updateCompany"
            disabled
          >
            <span slot="noOptions">{{ $t("messages.no_results") }}</span>
            <span slot="noResult">{{ $t("messages.no_results") }}</span>
          </multi-select>
        </b-form-group>
      </b-col>
    </b-row>
    <b-row>
      <b-col>
        <b-form-group :label="$t('field.name')">
          <b-form-input
            v-model="item.name"
            name="name"
            type="text"
            required
            placeholder="Lorem ipsum set dolor"
          />
        </b-form-group>
      </b-col>
      <b-col>
        <b-form-group :label="$t('field.number_approvals')">
          <b-form-input
            v-model="item.number_approvals"
            name="number_approvals"
            type="number"
            required
            placeholder="2"
            @input="changeNumberApprovals"
          />
        </b-form-group>
      </b-col>
    </b-row>

    <b-form-group :label="$t('field.users')">
      <multi-select
        v-model="item.users"
        :multiple="true"
        :options="usersList"
        :allow-empty="true"
        :searchable="true"
        :placeholder="$t('actions.pick_users')"
        :show-labels="false"
        label="name"
        track-by="id"
        @input="updateUsers"
      >
        <span slot="noOptions">{{ $t("messages.no_results") }}</span>
        <span slot="noResult">{{ $t("messages.no_results") }}</span>
      </multi-select>

      <p class="mt-4 font-weight-bold text-uppercase" v-if="hasUsers">
        {{ $t("field.order") }}:
      </p>

      <draggable
        v-model="item.users"
        v-bind="dragOptions"
        @input="changeOrder"
        v-if="hasUsers"
      >
        <transition-group class="d-inline-flex flex-column" type="transition">
          <div
            v-for="element in item.users"
            :key="element.id"
            class="element rounded-pill py-2 pr-2 pl-3 my-2 d-inline-flex justify-content-between align-items-center text-dark bg-white border shadow-sm"
          >
            {{ element.name }}
            <span
              class="px-2 rounded-circle bg-primary text-white ml-3 fs-14"
              >{{ element.order }}</span
            >
          </div>
        </transition-group>
      </draggable>
    </b-form-group>
  </div>
  <div v-else>
    <spinner />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import MultiSelect from 'vue-multiselect'
import usersApi from '@/api/users'
import companiesApi from '@/api/companies'
import draggable from 'vuedraggable'

export default {
  components: {
    MultiSelect,
    draggable
  },
  watch: {
    '$store.state.company.activeCompany': {
      async handler() {
        this.loading = true

        await this.init()

        this.loading = false
      }
    }
  },
  data: () => ({
    usersApi,
    companiesApi,
    loading: true,
    companies: [],
    company: null
  }),
  created() {
    this.init()
  },
  computed: {
    ...mapGetters({
      item: 'form/fields',
      activeCompany: 'company/activeCompany'
    }),
    dragOptions() {
      return {
        animation: 200,
        group: 'description',
        disabled: false,
        ghostClass: 'ghost'
      }
    },
    hasUsers() {
      return this.item && this.item.users && this.item.users.length
    },
    filterData() {
      return { role: 'super-admin,admin', company_id: this.activeCompany.id }
    }
  },
  methods: {
    async init() {
      await Promise.all([this.getUsers(), this.getCompanies()])

      this.setUsers()
      this.setCompany()

      this.loading = false
    },
    changeNumberApprovals(number) {
      return this.item ? (this.item.number_approvals = parseInt(number)) : ''
    },
    async getUsers() {
      try {
        const response = await this.usersApi.all(this.filterData)

        if (response && response.data && response.data.list) {
          this.usersList = response.data.list.map(user => ({
            ...user,
            order: null,
            user_id: user.id
          }))
        } else {
          this.$toasted.error(this.$t('messages.unknown_error'))
        }
      } catch (e) {
        console.error(e)
      }
    },
    setUsers() {
      if (this.item.users) {
        if (this.item.users.order !== null) {
          this.item.users = this.item.users.map(user => ({
            ...user,
            order: user.pivot.order,
            user_id: user.id
          }))

          this.item.users.sort((a, b) => (a.order > b.order ? 1 : -1))
        }
      }
    },
    updateUsers(selectedUsers) {
      this.item.users = selectedUsers
      this.formatUsers()
    },
    async getCompanies() {
      try {
        const response = await this.companiesApi.all()

        if (response && response.data && response.data.list) {
          this.companies = response.data.list.map(item => ({
            id: item.id,
            label: item.name
          }))
        } else {
          this.$toasted.error(this.$t('messages.unknown_error'))
        }
      } catch (e) {
        console.error(e)
      }
    },
    setCompany() {
      this.company = this.item.company_id
        ? this.companies.find(item => item.id === this.item.company_id)
        : this.companies.find(item => item.id === this.activeCompany.id)

      this.item.company_id = this.company.id

      if (this.company === undefined) {
        this.$toasted.error(
          this.$t('messages.not_found_foreign', {
            field: this.$t('field.company').toLowerCase()
          })
        )
      }
    },
    updateCompany(company) {
      this.item.company_id = company.id
      this.item.company = company
    },
    changeOrder(user) {
      user.map((user, index) => {
        this.item.users.filter(item => {
          if (user.id === item.id) {
            item.order = ++index
          }
        })
      })
    },
    formatUsers() {
      if (this.item.users) {
        this.item.users = this.item.users.map((user, index) => ({
          ...user,
          order: ++index,
          user_id: user.id
        }))
      }
      return this.item.users
    }
  }
}
</script>
<style lang="scss" scoped>
.element {
  cursor: all-scroll;
}
</style>